import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
} from "../../components/ContentLayouts";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgMicrochipByPQ from "../../assets/images/content/pet-microchipping/microchip-bypq.jpg";
import imgMicrochipSafety from "../../assets/images/content/pet-microchipping/microchip-safety.jpg";

const faqs = [
  {
    question: "What is pet microchipping, and why does my pet need it?",
    answer:
      "A microchip is a small implant that carries a unique ID number linked to your contact information. It's one of the most reliable ways to prove ownership and reconnect with your pet if they ever get lost, since collars and tags can fall off or be removed.",
  },
  {
    question: "How much does pet microchipping cost near me?",
    answer:
      "Cost usually depends on the clinic, whether it's combined with another visit, and registration charges. We show you the exact pricing at nearby clinics before you book, so there's nothing hidden.",
  },
  {
    question: "Where can I find pet microchipping near me?",
    answer:
      "Through PetQura, you simply share your location and the service you need, and we match you with verified clinics closest to you in Bangalore.",
  },
  {
    question: "Does microchipping hurt my pet?",
    answer:
      "It causes brief, mild discomfort, similar to a routine injection. No anaesthesia is required, and most pets are back to normal within minutes.",
  },
  {
    question: "How long does the microchipping procedure take?",
    answer:
      "The actual implantation takes just a few minutes. Including the vet check-up and registration, most appointments are completed in under 15 minutes.",
  },
  {
    question: "How do I register my pet's microchip after implantation?",
    answer:
      "The clinic registers your pet's microchip and contact details at the time of the procedure. You'll receive a confirmation with the registration number, which you should keep for your records.",
  },
  {
    question: "Can I update my contact details if I move or change my number?",
    answer:
      "Yes. It's important to keep your pet's microchip details current, and we can guide you on updating your registered information whenever your contact details change.",
  },
  {
    question: "Why should I choose PetQura for my pet's microchipping needs?",
    answer:
      "We take the guesswork out of finding the right clinic by matching you with the closest, most suitable option based on your needs. It saves time, reduces stress, and makes the whole process easier for you and your pet.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetMicrochipping = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Microchipping in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Find pet microchipping near you in Bangalore. Get your dog or cat microchipped at a trusted veterinary clinic and keep your pet safe and permanently identified."
        />
        <link
          rel="canonical"
          href="https://petqura.com/services/pet-microchipping-bangalore"
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Microchipping Services in Bangalore</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <TwoColRow>
            <ColText>
              <p>
                Losing a pet, even for a few hours, is one of the most
                stressful things a pet parent can go through. That's why so
                many pet parents across Bangalore search for{" "}
                <em>pet microchipping near me</em> before bringing a new
                puppy or kitten home, or right after realising their pet
                hasn't been chipped yet. A microchip is a tiny, rice-sized
                device placed under your pet's skin that carries a unique ID
                number linked to your contact details. If your pet ever
                wanders off, any vet or shelter can scan the chip and reach
                out to you directly. At PetQura, we connect you with verified
                veterinary clinics across Bangalore that offer microchipping,
                based on your location and the exact service you need, so
                you're not left guessing where to go.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgMicrochipByPQ}
                alt="Pet microchipping services by PetQura in Bangalore"
              />
            </ColImage>
          </TwoColRow>
        </HeroSection>

        {/* ── How It Works ── */}
        <ContentSection>
          <SectionHeading>How Pet Microchipping Works</SectionHeading>
          <p>
            We keep the process simple, mostly because we know pet parents
            already have enough on their plate. Once you book an appointment
            through PetQura, here's what typically happens at the clinic:
          </p>
          <BulletList>
            <li>
              A vet briefly examines your pet to confirm they're fit for the
              procedure
            </li>
            <li>
              The microchip, about the size of a grain of rice, is placed
              just under the skin between the shoulder blades using a sterile
              applicator
            </li>
            <li>
              The chip's unique ID is recorded and linked to your name, phone
              number, and address
            </li>
            <li>
              Your pet's details are entered into the pet registry so the
              chip can be traced later
            </li>
            <li>
              You receive a confirmation with the registration number for
              your own records
            </li>
          </BulletList>
          <p>
            Most visits are done in under 15 minutes, and your pet can go back
            to their usual routine right away.
          </p>
        </ContentSection>

        {/* ── Safety ── */}
        <ContentSection>
          <SectionHeading>Is Microchipping Safe for My Pet?</SectionHeading>
          <p>
            Yes, microchipping is safe, and the discomfort is minimal, similar
            to a routine vaccination. No anaesthesia is needed, and the chip
            itself has no battery or moving parts, so there's nothing to
            charge, replace, or maintain once it's in place. Most vets
            recommend microchipping once your puppy or kitten is old enough
            for their first round of vaccinations, though older pets can be
            chipped too, provided they're healthy. We work only with clinics
            that follow proper hygiene and handling standards, so you can feel
            confident your pet is in safe hands from the moment you walk in.
          </p>
        </ContentSection>

        {/* ── Cost ── */}
        <ContentSection>
          <SectionHeading>What Affects Pet Microchip Cost</SectionHeading>
          <p>
            We believe pet parents deserve to know what they're paying for
            before they book. The cost isn't fixed, and a few factors usually
            decide the final amount:
          </p>
          <BulletList>
            <li>The clinic's consultation charges</li>
            <li>
              Whether microchipping is done alone or combined with a
              vaccination or check-up visit
            </li>
            <li>Registration and database fees for recording your pet's details</li>
            <li>Any add-on services you choose during the same visit</li>
          </BulletList>
          <p>
            Since we work with multiple clinics, we show you the cost for each
            nearby option upfront, so there are no surprises when you arrive
            for the appointment.
          </p>
        </ContentSection>

        {/* ── Benefits ── */}
        <ContentSection>
          <SectionHeading>Benefits of Microchipping With PetQura</SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                <strong>A Better Chance of Bringing Your Pet Home Safely</strong>
                <br />
                A collar or tag can slip off or get removed, but a microchip
                stays with your pet for life. If your pet ever goes missing
                and someone finds them, a quick scan at any vet clinic or
                shelter pulls up your contact details right away. This single
                step significantly improves the odds of a lost pet being
                reunited with their family, often within hours instead of
                days. For us, this is the biggest reason microchipping
                matters, and it's why we make it as easy as possible for pet
                parents in Bangalore to get their pets chipped without any
                hassle.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgMicrochipSafety}
                alt="A better chance of bringing your pet home safely with a microchip"
              />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── Finding Near You ── */}
        <ContentSection>
          <SectionHeading>
            One Simple Way to Find Pet Microchipping Near You
          </SectionHeading>
          <p>
            Instead of calling around to different clinics or scrolling
            through reviews, we do that work for you. Tell us what service
            you're looking for, and we match you with the microchipping
            clinic nearest to you based on your exact location in Bangalore.
            This makes it easier to schedule appointments that fit your day,
            cuts down on travel time, and takes away a lot of the
            back-and-forth that usually comes with finding the right vet. Less
            stress for you, and a calmer visit for your pet too.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetMicrochipping;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
